import React from "react";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";

//components:
import {makeStyles, useTheme} from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import FirstPageIcon from "@material-ui/icons/FirstPage";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import LastPageIcon from "@material-ui/icons/LastPage";
import TablePagination from "@material-ui/core/TablePagination";

const useStyles1 = makeStyles(theme => ({
    root: {
        flexShrink: 0,
        color: theme.palette.text.primary,
        marginLeft: theme.spacing(2.5),
    },
}));

const PaginationActions = props => {
    const classes = useStyles1();
    const theme = useTheme();
    const {count, page, rowsPerPage, onChangePage} = props;

    function handleFirstPageButtonClick(event) {
        onChangePage(event, 0);
    }

    function handleBackButtonClick(event) {
        onChangePage(event, page - 1);
    }

    function handleNextButtonClick(event) {
        onChangePage(event, page + 1);
    }

    function handleLastPageButtonClick(event) {
        onChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    }

    return (
        <div className={classes.root}>
            <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="First Page"
                className="mr-2 bg-hover-primary text-hover-white p-2"
            >
                {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
            </IconButton>
            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label="Previous Page"
                className="mr-2 bg-hover-primary text-hover-white p-2"
            >
                {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="Next Page"
                className="mr-2 bg-hover-primary text-hover-white p-2"
            >
                {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </IconButton>
            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="Last Page"
                className="mr-2 bg-hover-primary text-hover-white p-2"
            >
                {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
            </IconButton>
        </div>
    );
};

PaginationActions.propTypes = {
    count: PropTypes.number,
    onChangePage: PropTypes.func.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
};

export const PaginationTable = ({setPage, setRowsPerPage, rows, rowsPerPage, page, rowsPerPageOptions, variant}) => {
    const {t} = useTranslation();

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            colSpan={12}
            count={rows.length}
            rowsPerPage={rowsPerPage}
            labelRowsPerPage={t("TABLE.PAGINATION.ROWS_PER_PAGE")}
            page={page}
            SelectProps={{
                inputProps: {
                    "aria-label": t("TABLE.PAGINATION.ROWS_PER_PAGE"),
                    className: "bg-hover-primary text-hover-white rounded font-size-1rem",
                },
                variant,
            }}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            ActionsComponent={PaginationActions}
        />
    );
};

PaginationTable.propTypes = {
    setPage: PropTypes.func.isRequired,
    setRowsPerPage: PropTypes.func.isRequired,
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number).isRequired,
    variant: PropTypes.string.isRequired,
};
